import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import type { ArchetypeId } from '@llura/types';

import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@llura/ui';
import { AnalyticsEvents, AnalyticsProps } from '@llura/constants';

import { Level1Breath } from './levels/level1-breath';
import { Level2PelvicPower } from './levels/level2-pelvic-power';
import { Level3Audio } from './levels/level3-audio';
import { PointsAwardedDialog } from './points-awarded-dialog';
import { SafeExitButton } from '../../shared/safe-exit-button';
import { useProgressStore, type LevelId, type ModeId } from '../../stores/progress.store';
import { useCoachStore } from '../../stores/coach.store';
import { useHaptic } from '../../hooks/useHaptic';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useStrings } from '../../hooks/useStrings';
import { useArchetypeTheme } from '../../hooks/useArchetypeTheme';
import { isArchetypeId } from '../../lib/archetypes';
import { trackEvent } from '../../lib/analytics';

const LEVEL_POINTS: Record<LevelId, number> = { 1: 10, 2: 15, 3: 25 };

const LEGACY_ROUTE_MAP: Record<string, ArchetypeId> = {
  structured: 'queen',
  edging: 'enchantress',
  exploratory: 'sensualist',
};

/**
 * Route guard. Validates `:mode`, redirecting legacy path ids onto their
 * archetype equivalents, and unknown ids back to the dashboard. All hooks
 * live in the inner component so they're only ever called with a valid
 * archetype.
 */
export function WorkoutPage() {
  const params = useParams();
  const rawMode = params.mode ?? '';

  const legacyTarget = LEGACY_ROUTE_MAP[rawMode];
  if (legacyTarget) {
    return <Navigate to={`/workout/${legacyTarget}`} replace />;
  }
  if (!isArchetypeId(rawMode)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <WorkoutPageBody mode={rawMode} />;
}

function WorkoutPageBody({ mode }: { mode: ModeId }) {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { strings } = useStrings();

  // Keep the screen awake for the whole session — the guided levels involve
  // long stretches with no touch input.
  useWakeLock();
  const completeSession = useProgressStore((s) => s.completeSession);
  const coachName = useCoachStore((s) => s.name);
  const theme = useArchetypeTheme(mode);

  const [level, setLevel] = useState<LevelId>(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [awarded, setAwarded] = useState(0);
  const startedAtRef = useRef<number>(performance.now());

  useEffect(() => {
    startedAtRef.current = performance.now();
    trackEvent(AnalyticsEvents.WorkoutStarted, {
      [AnalyticsProps.Mode]: mode,
      [AnalyticsProps.Level]: level,
    });
  }, [mode, level]);

  const handleComplete = (atLevel: LevelId) => {
    const points = LEVEL_POINTS[atLevel];
    completeSession(mode, atLevel, points);
    haptic.success();
    setAwarded(points);
    setDialogOpen(true);
    trackEvent(AnalyticsEvents.WorkoutCompleted, {
      [AnalyticsProps.Mode]: mode,
      [AnalyticsProps.Level]: atLevel,
      [AnalyticsProps.PointsAwarded]: points,
      [AnalyticsProps.DurationMs]: Math.round(performance.now() - startedAtRef.current),
    });
  };

  const ritualHeadline = useMemo(
    () => strings.archetypes[mode].ritualHeadline,
    [strings.archetypes, mode],
  );

  return (
    <div
      className="mx-auto w-full max-w-md px-4 pb-6"
      style={theme.style}
      data-archetype={theme.dataArchetype}
    >
      <header className="flex items-center gap-2 py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="rounded-full"
          aria-label={strings.ritual.backAria}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {strings.ritual.header}
          </div>
          <div className="font-serif text-xl leading-tight">{ritualHeadline}</div>
        </div>
        <Badge variant="outline" className="border-border text-muted-foreground">
          {String(level)}/3
        </Badge>
      </header>

      <div className="mb-2 flex justify-end">
        <SafeExitButton />
      </div>

      <Tabs
        value={String(level)}
        onValueChange={(v) => setLevel(Number(v) as LevelId)}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-3 rounded-full bg-card p-1">
          <TabsTrigger value="1" className="rounded-full">
            {strings.ritual.levels[0]}
          </TabsTrigger>
          <TabsTrigger value="2" className="rounded-full">
            {strings.ritual.levels[1]}
          </TabsTrigger>
          <TabsTrigger value="3" className="rounded-full">
            {strings.ritual.levels[2]}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="1" className="mt-6">
          <Level1Breath archetypeId={mode} onComplete={() => handleComplete(1)} />
        </TabsContent>
        <TabsContent value="2" className="mt-6">
          <Level2PelvicPower archetypeId={mode} onComplete={() => handleComplete(2)} />
        </TabsContent>
        <TabsContent value="3" className="mt-6">
          <Level3Audio onComplete={() => handleComplete(3)} />
        </TabsContent>
      </Tabs>

      <PointsAwardedDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        points={awarded}
        coachName={coachName}
        onContinue={() => {
          if (level < 3) setLevel((level + 1) as LevelId);
          else navigate('/afterglow', { state: { archetypeId: mode } });
        }}
      />
    </div>
  );
}
